/**
 * API utility functions for FastAPI backend
 */

// API base URL - uses environment variable in production, localhost in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Get auth token from localStorage
export const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

// Set auth token in localStorage
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

// Remove auth token
export const removeAuthToken = () => {
  localStorage.removeItem('auth_token');
};

// Create headers with auth token
const getHeaders = (includeAuth = true, contentType = 'application/json') => {
  const headers = {
    'Content-Type': contentType,
  };
  
  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
};

// API request wrapper
export const apiRequest = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    body = null,
    includeAuth = true,
    contentType = 'application/json',
    ...restOptions
  } = options;

  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    method,
    headers: getHeaders(includeAuth, contentType),
    ...restOptions,
  };

  if (body && method !== 'GET') {
    if (body instanceof FormData) {
      // Remove Content-Type header for FormData (browser sets it with boundary)
      delete config.headers['Content-Type'];
      config.body = body;
    } else if (contentType === 'multipart/form-data') {
      // For multipart, don't set Content-Type - let browser set it
      delete config.headers['Content-Type'];
      config.body = body;
    } else if (contentType === 'application/json') {
      config.body = JSON.stringify(body);
    } else {
      config.body = body;
    }
  }

  try {
    const response = await fetch(url, config);
    
    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401) {
      removeAuthToken();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new Error('Unauthorized');
    }

    // Parse JSON response
    let data;
    try {
      data = await response.json();
    } catch (e) {
      // If response is not JSON, create error message from status
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
      }
      throw new Error('Invalid response from server');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || data.message || `Request failed with status ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

// Login function - uses OAuth2PasswordRequestForm format
export const login = async (email, password) => {
  const formData = new URLSearchParams();
  formData.append('username', email); // OAuth2 uses 'username' field
  formData.append('password', password);
  
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Login failed');
  }

  const data = await response.json();
  setAuthToken(data.access_token);
  return data;
};

// Register function
export const register = async (name, email, password) => {
  return apiRequest('/api/auth/register', {
    method: 'POST',
    body: { name, email, password },
    includeAuth: false,
  });
};

// Logout function
export const logout = async () => {
  try {
    await apiRequest('/api/auth/logout', {
      method: 'POST',
    });
  } finally {
    removeAuthToken();
  }
};

// Poll a Celery task status
// Returns { task_id, job_id, job_type, status, result, error_message }
export const getTaskStatus = (taskId) =>
  apiRequest(`/api/algorithms/status/${taskId}`);


